/**
=========================================================
* Soft UI Dashboard PRO React - v3.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

/**
 * The base typography styles for the Soft UI Dashboard PRO Material.
 * You can add new typography style using this file.
 * You can customized the typography styles for the entire Soft UI Dashboard PRO Material using thie file.
 */

// Soft UI Dashboard PRO React Base Styles
import colors from './colors';

// Soft UI Dashboard PRO React Helper Functions
import pxToRem from '../functions/pxToRem';
import { Typography } from '../../types';
import {
    fontSizeValues,
    fontWeightValues,
    letterSpacingValues,
    lineHeightValues,
} from '../design-system/tokens';

const { body } = colors;

export const DESIGN_SYSTEM_TYPOGRAPHY_VARIANTS = {
    h1: 'ds.h1',
    h2: 'ds.h2',
    h3: 'ds.h3',
    h4: 'ds.h4',
    h5: 'ds.h5',
    h6: 'ds.h6',
    xl: 'ds.xl',
    lg: 'ds.lg',
    md: 'ds.md',
    sm: 'ds.sm',
    xs: 'ds.xs',
    xxs: 'ds.xxs',
} as const;

const baseProperties = {
    fontFamily: '"Geist Variable", "Helvetica", "Arial", sans-serif',
    fontWeightLight: fontWeightValues.light,
    fontWeightRegular: fontWeightValues.regular,
    fontWeightMedium: fontWeightValues.medium,
    fontWeightSemibold: fontWeightValues.semibold,
    fontWeightBold: fontWeightValues.bold,
    fontSizeXXS: pxToRem(10),
    fontSizeXS: pxToRem(12),
    fontSizeSM: pxToRem(14),
    fontSizeMD: pxToRem(16),
    fontSizeLG: pxToRem(18),
    fontSizeXL: pxToRem(20),
};

const baseHeadingProperties = {
    fontFamily: baseProperties.fontFamily,
    color: body.main,
    fontWeight: baseProperties.fontWeightRegular,
};

const baseDisplayProperties = {
    fontFamily: baseProperties.fontFamily,
    color: body.main,
    fontWeight: baseProperties.fontWeightLight,
    lineHeight: 1.2,
};

const typography: Typography = {
    fontFamily: baseProperties.fontFamily,
    fontWeightLight: baseProperties.fontWeightLight,
    fontWeightRegular: baseProperties.fontWeightRegular,
    fontWeightMedium: baseProperties.fontWeightMedium,
    fontWeightSemibold: baseProperties.fontWeightSemibold,
    fontWeightBold: baseProperties.fontWeightBold,

    h1: {
        fontSize: pxToRem(48),
        lineHeight: pxToRem(64),
        ...baseHeadingProperties,
    },

    h2: {
        fontSize: pxToRem(36),
        lineHeight: pxToRem(44),
        ...baseHeadingProperties,
    },

    h3: {
        fontSize: pxToRem(30),
        lineHeight: pxToRem(40),
        ...baseHeadingProperties,
    },

    h4: {
        fontSize: pxToRem(24),
        lineHeight: pxToRem(32),
        ...baseHeadingProperties,
    },

    h5: {
        fontSize: pxToRem(20),
        lineHeight: pxToRem(28),
        ...baseHeadingProperties,
    },

    h6: {
        fontSize: pxToRem(16),
        lineHeight: pxToRem(24),
        ...baseHeadingProperties,
    },

    subtitle1: {
        fontFamily: baseProperties.fontFamily,
        fontSize: baseProperties.fontSizeXL,
        fontWeight: baseProperties.fontWeightRegular,
        lineHeight: pxToRem(32),
    },

    subtitle2: {
        fontFamily: baseProperties.fontFamily,
        fontSize: baseProperties.fontSizeMD,
        fontWeight: baseProperties.fontWeightRegular,
        lineHeight: pxToRem(24),
    },

    body1: {
        fontFamily: baseProperties.fontFamily,
        fontSize: baseProperties.fontSizeMD,
        fontWeight: baseProperties.fontWeightRegular,
        lineHeight: pxToRem(24),
    },

    body2: {
        fontFamily: baseProperties.fontFamily,
        fontSize: baseProperties.fontSizeSM,
        fontWeight: baseProperties.fontWeightRegular,
        lineHeight: pxToRem(20),
    },

    button: {
        fontFamily: baseProperties.fontFamily,
        fontSize: baseProperties.fontSizeMD,
        fontWeight: baseProperties.fontWeightBold,
        lineHeight: pxToRem(20),
        textTransform: 'uppercase',
    },

    caption: {
        fontFamily: baseProperties.fontFamily,
        fontSize: baseProperties.fontSizeXS,
        fontWeight: baseProperties.fontWeightRegular,
        lineHeight: pxToRem(16),
    },

    overline: {
        fontFamily: baseProperties.fontFamily,
        fontSize: baseProperties.fontSizeXS,
        fontWeight: baseProperties.fontWeightRegular,
        lineHeight: pxToRem(32),
    },

    d1: {
        fontSize: pxToRem(80),
        ...baseDisplayProperties,
    },

    d2: {
        fontSize: pxToRem(72),
        ...baseDisplayProperties,
    },

    d3: {
        fontSize: pxToRem(64),
        ...baseDisplayProperties,
    },

    d4: {
        fontSize: pxToRem(56),
        ...baseDisplayProperties,
    },

    d5: {
        fontSize: pxToRem(48),
        ...baseDisplayProperties,
    },

    d6: {
        fontSize: pxToRem(40),
        ...baseDisplayProperties,
    },

    size: {
        xxs: baseProperties.fontSizeXXS,
        xs: baseProperties.fontSizeXS,
        sm: baseProperties.fontSizeSM,
        md: baseProperties.fontSizeMD,
        lg: baseProperties.fontSizeLG,
        xl: baseProperties.fontSizeXL,
    },

    lineHeight: {
        sm: 1.25,
        md: 1.5,
        lg: 2,
    },

    [DESIGN_SYSTEM_TYPOGRAPHY_VARIANTS.h1]: {
        fontFamily: baseProperties.fontFamily,
        fontSize: fontSizeValues.h1,
        lineHeight: lineHeightValues.h1,
        letterSpacing: letterSpacingValues.h1,
        fontWeight: fontWeightValues.regular,
    },
    [DESIGN_SYSTEM_TYPOGRAPHY_VARIANTS.h2]: {
        fontFamily: baseProperties.fontFamily,
        fontSize: fontSizeValues.h2,
        lineHeight: lineHeightValues.h2,
        letterSpacing: letterSpacingValues.h2,
        fontWeight: fontWeightValues.regular,
    },
    [DESIGN_SYSTEM_TYPOGRAPHY_VARIANTS.h3]: {
        fontFamily: baseProperties.fontFamily,
        fontSize: fontSizeValues.h3,
        lineHeight: lineHeightValues.h3,
        letterSpacing: letterSpacingValues.h3,
        fontWeight: fontWeightValues.regular,
    },
    [DESIGN_SYSTEM_TYPOGRAPHY_VARIANTS.h4]: {
        fontFamily: baseProperties.fontFamily,
        fontSize: fontSizeValues.h4,
        lineHeight: lineHeightValues.h4,
        letterSpacing: letterSpacingValues.h4,
        fontWeight: fontWeightValues.regular,
    },
    [DESIGN_SYSTEM_TYPOGRAPHY_VARIANTS.h5]: {
        fontFamily: baseProperties.fontFamily,
        fontSize: fontSizeValues.h5,
        lineHeight: lineHeightValues.h5,
        letterSpacing: letterSpacingValues.h5,
        fontWeight: fontWeightValues.regular,
    },
    [DESIGN_SYSTEM_TYPOGRAPHY_VARIANTS.h6]: {
        fontFamily: baseProperties.fontFamily,
        fontSize: fontSizeValues.h6,
        lineHeight: lineHeightValues.h6,
        letterSpacing: letterSpacingValues.h6,
        fontWeight: fontWeightValues.regular,
    },
    [DESIGN_SYSTEM_TYPOGRAPHY_VARIANTS.xl]: {
        fontFamily: baseProperties.fontFamily,
        fontSize: fontSizeValues.xl,
        lineHeight: lineHeightValues.xl,
        letterSpacing: letterSpacingValues.xl,
        fontWeight: fontWeightValues.regular,
    },
    [DESIGN_SYSTEM_TYPOGRAPHY_VARIANTS.lg]: {
        fontFamily: baseProperties.fontFamily,
        fontSize: fontSizeValues.lg,
        lineHeight: lineHeightValues.lg,
        letterSpacing: letterSpacingValues.lg,
        fontWeight: fontWeightValues.regular,
    },
    [DESIGN_SYSTEM_TYPOGRAPHY_VARIANTS.md]: {
        fontFamily: baseProperties.fontFamily,
        fontSize: fontSizeValues.md,
        lineHeight: lineHeightValues.md,
        letterSpacing: letterSpacingValues.md,
        fontWeight: fontWeightValues.regular,
    },
    [DESIGN_SYSTEM_TYPOGRAPHY_VARIANTS.sm]: {
        fontFamily: baseProperties.fontFamily,
        fontSize: fontSizeValues.sm,
        lineHeight: lineHeightValues.sm,
        letterSpacing: letterSpacingValues.sm,
        fontWeight: fontWeightValues.regular,
    },
    [DESIGN_SYSTEM_TYPOGRAPHY_VARIANTS.xs]: {
        fontFamily: baseProperties.fontFamily,
        fontSize: fontSizeValues.xs,
        lineHeight: lineHeightValues.xs,
        letterSpacing: letterSpacingValues.xs,
        fontWeight: fontWeightValues.regular,
    },
    [DESIGN_SYSTEM_TYPOGRAPHY_VARIANTS.xxs]: {
        fontFamily: baseProperties.fontFamily,
        fontSize: fontSizeValues.xxs,
        lineHeight: lineHeightValues.xxs,
        letterSpacing: letterSpacingValues.xxs,
        fontWeight: fontWeightValues.regular,
    },
};

export default typography;
